import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiSearch, FiChevronDown, FiCheck } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Confetti from './Confetti';
import api from '../services/api';

const TaskModal = ({ onClose, onSuccess }) => {
  const { isAdmin } = useAuth();
  const { showSuccess, showError } = useToast();
  const [teams, setTeams] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    team_id: '',
    assigned_to: [],
    priority: 'medium',
    due_date: '',
    tags: [],
    reminder_date: '',
    is_recurring: false,
    recurrence_day_of_month: new Date().getDate(),
  });
  const [newTag, setNewTag] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  const memberDropdownRef = useRef(null);

  useEffect(() => {
    fetchTeams();
  }, []);

  useEffect(() => {
    if (formData.team_id) {
      fetchTeamMembers(formData.team_id);
    } else {
      setTeamMembers([]);
    }
  }, [formData.team_id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (memberDropdownRef.current && !memberDropdownRef.current.contains(event.target)) {
        setShowMemberDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await api.get('/teams');
      setTeams(response.data.teams || []);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchTeamMembers = async (teamId) => {
    try {
      const response = await api.get(`/teams/${teamId}`);
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = {
        ...formData,
        team_id: parseInt(formData.team_id),
        assigned_to: formData.assigned_to.map(id => parseInt(id)),
        tags: formData.tags,
        reminder_date: formData.reminder_date || null,
      };
      if (formData.is_recurring) {
        payload.is_recurring = true;
        payload.recurrence_day_of_month = formData.recurrence_day_of_month;
      }
      const response = await api.post('/tasks', payload);
      
      // Find assigned user names for toast
      const assignedUsers = teamMembers.filter(m => 
        formData.assigned_to.includes(m.id.toString())
      );
      const assignedUserNames = assignedUsers.map(u => u.name).join(', ') || 'team members';
      
      // Show confetti animation
      setShowConfetti(true);
      
      showSuccess(
        formData.is_recurring
          ? `Recurring task "${formData.title}" created! A new instance will appear each month.`
          : `Task "${formData.title}" has been assigned to ${assignedUserNames}! 🎉`,
        formData.is_recurring ? 'Recurring Task Created' : 'Task Assigned Successfully!'
      );
      
      // Close modal after animation completes (longer on mobile)
      const isMobile = window.innerWidth < 1024;
      setTimeout(() => {
        onSuccess();
        onClose();
      }, isMobile ? 2000 : 1500);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error creating task';
      setError(errorMsg);
      showError(errorMsg, 'Failed to Assign Task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {showConfetti && (
        <Confetti key="confetti" show={showConfetti} onComplete={() => setShowConfetti(false)} />
      )}
      <motion.div
        key="modal"
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: '100%', scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: '100%', scale: 0.95 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md p-5 sm:p-6 max-h-[90vh] overflow-y-auto touch-manipulation"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Create Task</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <FiX size={24} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter task title"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Enter task description"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Team *
              </label>
              <select
                value={formData.team_id}
                onChange={(e) => setFormData({ ...formData, team_id: e.target.value, assigned_to: [] })}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Select a team</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Assign To * (Select multiple)
              </label>
              <div className="relative" ref={memberDropdownRef}>
                {/* Selected members display */}
                {formData.assigned_to.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {formData.assigned_to.map(userId => {
                      const member = teamMembers.find(m => m.id.toString() === userId);
                      return member ? (
                        <span
                          key={userId}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-700 font-medium"
                        >
                          {member.name}
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                assigned_to: formData.assigned_to.filter(id => id !== userId),
                              });
                            }}
                            className="ml-2 text-primary-700 hover:text-primary-900 font-bold"
                          >
                            ×
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}

                {/* Search input and dropdown */}
                <div className="relative">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      value={memberSearch}
                      onChange={(e) => {
                        setMemberSearch(e.target.value);
                        setShowMemberDropdown(true);
                      }}
                      onFocus={() => setShowMemberDropdown(true)}
                      placeholder={formData.team_id ? "Search team members..." : "Select a team first"}
                      disabled={!formData.team_id}
                      className={`w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                        showMemberDropdown && formData.team_id ? 'rounded-b-none border-b-0' : ''
                      }`}
                    />
                    <FiChevronDown 
                      className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 transition-transform ${
                        showMemberDropdown ? 'rotate-180' : ''
                      }`} 
                      size={18}
                    />
                  </div>

                  {/* Dropdown menu */}
                  <AnimatePresence>
                    {showMemberDropdown && formData.team_id && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute z-10 w-full mt-0 bg-white border border-gray-300 border-t-0 rounded-b-lg shadow-lg max-h-60 overflow-y-auto"
                      >
                        {(() => {
                          const activeMembers = teamMembers.filter(m => m.is_active !== false);
                          const filteredMembers = activeMembers.filter(member =>
                            member.name.toLowerCase().includes(memberSearch.toLowerCase())
                          );

                          if (filteredMembers.length === 0) {
                            return (
                              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                No members found
                              </div>
                            );
                          }

                          return filteredMembers.map((member) => {
                            const isSelected = formData.assigned_to.includes(member.id.toString());
                            return (
                              <button
                                key={member.id}
                                type="button"
                                onClick={() => {
                                  if (isSelected) {
                                    setFormData({
                                      ...formData,
                                      assigned_to: formData.assigned_to.filter(id => id !== member.id.toString()),
                                    });
                                  } else {
                                    setFormData({
                                      ...formData,
                                      assigned_to: [...formData.assigned_to, member.id.toString()],
                                    });
                                  }
                                }}
                                className={`w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                                  isSelected ? 'bg-primary-50' : ''
                                }`}
                              >
                                <span className={`${isSelected ? 'font-medium text-primary-700' : 'text-gray-700'}`}>
                                  {member.name}
                                </span>
                                {isSelected && (
                                  <FiCheck className="text-primary-600" size={18} />
                                )}
                              </button>
                            );
                          });
                        })()}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {formData.assigned_to.length === 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.team_id ? 'Search and select team members' : 'Select a team first'}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Priority
              </label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            {!formData.is_recurring && (
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Due Date
              </label>
              <input
                type="datetime-local"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            )}

            <div className="border-t border-gray-200 pt-4">
              <label className="flex items-center gap-2 cursor-pointer mb-3">
                <input
                  type="checkbox"
                  checked={formData.is_recurring}
                  onChange={(e) => setFormData({ ...formData, is_recurring: e.target.checked })}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-gray-700 text-sm font-medium">Repeat monthly (same date every month)</span>
              </label>
              {formData.is_recurring && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-gray-600 text-sm">On day</span>
                  <select
                    value={formData.recurrence_day_of_month}
                    onChange={(e) => setFormData({ ...formData, recurrence_day_of_month: Number(e.target.value) })}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <span className="text-gray-600 text-sm">of each month</span>
                </div>
              )}
              {formData.is_recurring && (
                <p className="text-xs text-gray-500 mt-2">A new task will open on that date each month; complete it for each month.</p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-700"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          tags: formData.tags.filter((_, i) => i !== index),
                        });
                      }}
                      className="ml-2 text-primary-700 hover:text-primary-900"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newTag.trim()) {
                      e.preventDefault();
                      setFormData({
                        ...formData,
                        tags: [...formData.tags, newTag.trim()],
                      });
                      setNewTag('');
                    }
                  }}
                  placeholder="Add tag (press Enter)"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (newTag.trim()) {
                      setFormData({
                        ...formData,
                        tags: [...formData.tags, newTag.trim()],
                      });
                      setNewTag('');
                    }
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all"
                >
                  Add
                </button>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Reminder Date (Optional)
              </label>
              <input
                type="datetime-local"
                value={formData.reminder_date}
                onChange={(e) => setFormData({ ...formData, reminder_date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="text-xs text-gray-500 mt-1">You'll receive an email reminder at this time</p>
            </div>

            <div className="flex items-center space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating...' : 'Create Task'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TaskModal;
